import { StandardEvents } from "@shopify/events";

export function normalizeVariantId(id) {
  return id == null
    ? ""
    : String(id).replace(/^gid:\/\/shopify\/ProductVariant\//, "");
}

function normalizeProductId(id) {
  return id == null
    ? ""
    : String(id).replace(/^gid:\/\/shopify\/Product(?:Variant)?\//, "");
}

function eventVariant(result, event) {
  return result?.variant || event?.detail?.variant;
}

function productIdFromPayload(result, event) {
  const detail = result?.detail || event?.detail;
  return (
    event?.product?.id ||
    detail?.productId ||
    detail?.resource?.productId ||
    detail?.variant?.productId
  );
}

function matchesProduct(root, result, event) {
  const productId = productIdFromPayload(result, event);
  return (
    !productId ||
    normalizeProductId(productId) === normalizeProductId(root.dataset.productId)
  );
}

class ReassuranceRows extends HTMLElement {
  connectedCallback() {
    if (this._listening) return;
    this._listening = true;
    if (!Number.isFinite(this._sequence)) this._sequence = 0;
    this.data = this.readData();
    this.onVariantUpdate = this.onVariantUpdate.bind(this);
    document.addEventListener(
      StandardEvents.productSelect,
      this.onVariantUpdate,
    );
  }
  disconnectedCallback() {
    this._sequence += 1;
    if (this._listening)
      document.removeEventListener(
        StandardEvents.productSelect,
        this.onVariantUpdate,
      );
    this._listening = false;
  }
  readData() {
    try {
      return JSON.parse(
        this.querySelector("[data-reassurance-data]")?.textContent || "{}",
      );
    } catch {
      return {};
    }
  }
  async onVariantUpdate(event) {
    if (event.product?.id && !matchesProduct(this, null, event)) return;
    const sequence = ++this._sequence;
    let result;
    try {
      result = await event.promise;
    } catch (error) {
      if (error?.name !== "AbortError")
        console.warn("[reassurance-rows]", error);
      return;
    }
    if (
      !this._listening ||
      sequence !== this._sequence ||
      !this.isConnected ||
      !matchesProduct(this, result, event)
    )
      return;
    const entry =
      this.data[normalizeVariantId(eventVariant(result, event)?.id)];
    const fill = this.querySelector("[data-progress-fill]");
    if (!entry || !fill) return;
    fill.style.width = `${entry.percent}%`;
    const prefix = this.querySelector("[data-progress-prefix]");
    const amount = this.querySelector("[data-progress-amount]");
    const suffix = this.querySelector("[data-progress-suffix]");
    if (entry.done) {
      prefix.textContent = this.dataset.progressComplete;
      amount.hidden = true;
      suffix.textContent = "";
    } else {
      prefix.textContent = this.dataset.progressPrefix;
      amount.hidden = false;
      amount.textContent = entry.amount;
      suffix.textContent = this.dataset.progressSuffix;
    }
  }
}

class VolumeTiers extends HTMLElement {
  connectedCallback() {
    if (this._listening) return;
    this._listening = true;
    if (!Number.isFinite(this._sequence)) this._sequence = 0;
    try {
      this.prices = JSON.parse(
        this.querySelector("[data-volume-tiers-prices]")?.textContent || "{}",
      );
    } catch {
      this.prices = {};
    }
    this.onVariantUpdate = this.onVariantUpdate.bind(this);
    document.addEventListener(
      StandardEvents.productSelect,
      this.onVariantUpdate,
    );
  }
  disconnectedCallback() {
    this._sequence += 1;
    if (this._listening)
      document.removeEventListener(
        StandardEvents.productSelect,
        this.onVariantUpdate,
      );
    this._listening = false;
  }
  async onVariantUpdate(event) {
    if (event.product?.id && !matchesProduct(this, null, event)) return;
    const sequence = ++this._sequence;
    let result;
    try {
      result = await event.promise;
    } catch (error) {
      if (error?.name !== "AbortError") console.warn("[volume-tiers]", error);
      return;
    }
    if (
      !this._listening ||
      sequence !== this._sequence ||
      !this.isConnected ||
      !matchesProduct(this, result, event)
    )
      return;
    const tierPrices =
      this.prices[normalizeVariantId(eventVariant(result, event)?.id)];
    if (!tierPrices) return;
    this.querySelectorAll("[data-tier-unit]").forEach((node) => {
      if (tierPrices[node.dataset.tierIndex])
        node.textContent = tierPrices[node.dataset.tierIndex];
    });
  }
}

if (!customElements.get("reassurance-rows"))
  customElements.define("reassurance-rows", ReassuranceRows);
if (!customElements.get("volume-tiers"))
  customElements.define("volume-tiers", VolumeTiers);
